import React, { useState, useContext, useEffect } from "react";
import { getUser, listAddresses } from '../graphql/queries'
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import styled from "@emotion/styled";
import { API , graphqlOperation} from 'aws-amplify'
import { createOrder, createMyProduct, createAddress, updateProduct } from '../graphql/mutations'

import { Notification } from 'element-react'
import BillingDetailsFields from "./prebuilt/BillingDetailsFields";
import SubmitButton from "./prebuilt/SubmitButton";
import CheckoutError from "./prebuilt/CheckoutError";
import { formatNumber } from '../utils';
import { UserContext } from '../App'
import { CartContext } from './contexts/CartContext';
import aws_exports from '../aws-exports';
import TaxInvoiceModal from './print/TaxInvoiceModal';


const FormCard = styled.form`
  background: #fff;
  width: 100%;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 16px;
  padding: 20px;

  @media screen and (min-width: 480px) {
    padding: 28px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px;
  font-size: 1.05rem;
  font-weight: 800;
  color: #1f2d3d;

  &:not(:first-of-type) {
    margin-top: 24px;
  }
`;

const CardBox = styled.div`
  padding: 14px;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  background: #fff;

  & .StripeElement {
    width: 100%;
  }
`;

const ButtonWrap = styled.div`
  margin-top: 22px;
`;

const SecureNote = styled.p`
  margin: 16px 0 0;
  text-align: center;
  font-size: 0.82rem;
  color: #8a99a8;
`;

const AddressOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const AddressOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid ${props => (props.$selected ? "#01bf71" : "#dfe3e8")};
  background: ${props => (props.$selected ? "rgba(1, 191, 113, 0.06)" : "#fff")};
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  input[type="radio"] {
    margin-top: 3px;
  }
`;

const AddressOptionBody = styled.div`
  min-width: 0;
`;

const AddressOptionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2d3d;
`;

const DefaultTag = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(1, 191, 113, 0.12);
  color: #0a8f57;
`;

const AddressOptionLine = styled.div`
  color: #6a7b8a;
  font-size: 0.88rem;
  margin-top: 2px;
`;

const SaveAddressRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 0.9rem;
  color: #46586a;
  cursor: pointer;
`;

const PromoGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 8px;

  input {
    flex: 1;
    padding: 10px 14px;
    border: 1px solid #dfe3e8;
    border-radius: 10px;
    font-size: 0.92rem;
    font-weight: 600;
    text-transform: uppercase;
    &:focus {
      outline: none;
      border-color: #01bf71;
    }
  }

  button {
    padding: 10px 18px;
    background: #1f2d3d;
    color: #fff;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #01bf71;
    }
  }
`;

const AppliedBadge = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: rgba(1, 191, 113, 0.08);
  border: 1px solid rgba(1, 191, 113, 0.25);
  border-radius: 10px;
  color: #0a8f57;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 8px;

  button {
    background: transparent;
    border: none;
    color: #e25950;
    font-weight: 700;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PromoMessage = styled.div`
  font-size: 0.84rem;
  margin-bottom: 12px;
  color: ${props => (props.$error ? '#e25950' : '#0a8f57')};
  font-weight: 600;
`;

const DEMO_COUPONS = {
  WELCOME10: { type: "PERCENTAGE", value: 10, label: "10% off entire order" },
  SAVE5: { type: "FLAT", value: 5, label: "$5.00 off entire order" },
  BOMBAY20: { type: "PERCENTAGE", value: 20, label: "20% off entire order" },
  FREESHIP: { type: "FLAT", value: 5, label: "Free Shipping ($5 off)" }
};



const CheckoutForm = ({ price, onSuccessfulCheckout, total, tax, onDiscountChange }) => {
  const [isProcessing, setProcessingTo] = useState(false);
  const [checkoutError, setCheckoutError] = useState();
  const { user } = useContext(UserContext)
  const {cartItems} = useContext(CartContext);
  const stripe = useStripe();
  const elements = useElements();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [saveNewAddress, setSaveNewAddress] = useState(false);

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [stripeDisabled, setStripeDisabled] = useState(false);

  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");

  const handleApplyPromo = async (e) => {
    if (e) e.preventDefault();
    setPromoError("");
    setPromoSuccess("");

    const cleanCode = promoCodeInput.trim().toUpperCase();
    if (!cleanCode) {
      setPromoError("Please enter a promo code.");
      return;
    }

    let couponData = DEMO_COUPONS[cleanCode];

    if (!couponData) {
      setPromoError("Invalid promo code. Try WELCOME10, SAVE5, or BOMBAY20.");
      return;
    }

    let discount = 0;
    const subtotalVal = parseFloat(total) || 0;
    if (couponData.type === "PERCENTAGE") {
      discount = (subtotalVal * couponData.value) / 100;
    } else {
      discount = couponData.value;
    }
    discount = Math.min(discount, parseFloat(price) || 0);

    const promoInfo = {
      code: cleanCode,
      discountAmount: discount,
      label: couponData.label
    };

    setAppliedPromo(promoInfo);
    setPromoSuccess(`Code ${cleanCode} applied! Saved ${formatNumber(discount)}.`);
    if (onDiscountChange) {
      onDiscountChange(promoInfo);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCodeInput("");
    setPromoError("");
    setPromoSuccess("");
    if (onDiscountChange) {
      onDiscountChange({ code: "", discountAmount: 0, label: "" });
    }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchInitialData = async () => {
      try {
        const { getStoreSettings: getStoreSettingsQuery } = require('../graphql/queries');
        const settingsRes = await API.graphql(graphqlOperation(getStoreSettingsQuery, { id: 'default' }));
        if (isMounted && settingsRes.data.getStoreSettings) {
          setStripeDisabled(settingsRes.data.getStoreSettings.stripeDisabled);
        }
      } catch (err) {
        console.warn('Error fetching store settings:', err);
      }

      try {
        const result = await API.graphql(graphqlOperation(listAddresses, { limit: 50 }))
        const items = result.data.listAddresses.items || []
        if (isMounted) {
          setAddresses(items)
          const defaultAddress = items.find(a => a.isDefault) || items[0]
          if (defaultAddress) setSelectedAddressId(defaultAddress.id)
        }
      } catch (error) {
        console.error('Error fetching saved addresses', error)
      }
    }
    fetchInitialData()
    return () => {
      isMounted = false
    }
  }, []);

  // TIP
  // use the cardElements onChange prop to add a handler
  // for setting any errors:

  const handleCardDetailsChange = ev => {
    ev.error ? setCheckoutError(ev.error.message) : setCheckoutError();
  };

  const getOwnerEmail = async ownerId => {
    try {
      const input = { id: ownerId }
      const result = await API.graphql(graphqlOperation(getUser, input))
      return result.data.getUser.email
    } catch (error) {
      console.error(`Error fetching product owner's email: `, error)
    }
  };
  
  const sendOwnerEmail = async ({ orderId, totalAmount, subtotal, taxAmount, billingDetails, items }) => {
    try {
      const bucket = aws_exports.aws_user_files_s3_bucket || 'amplify-indiancommerce-dev-172421-deployment';
      const region = aws_exports.aws_user_files_s3_bucket_region || aws_exports.aws_project_region || 'us-east-1';

      const toDollar = (v) => {
        if (v === undefined || v === null || v === '') return '0.00';
        if (typeof v === 'string' && v.startsWith('$')) v = v.replace('$', '');
        const n = Number(v);
        if (isNaN(n)) return '0.00';
        return (n > 100 || Number.isInteger(n)) ? (n / 100).toFixed(2) : n.toFixed(2);
      };

      const emailItems = (items || []).map(item => {
        const itemDesc = item.description || item.name || 'Product Item';
        const fileKey = item.file && typeof item.file === 'object' && item.file.key ? item.file.key : (typeof item.file === 'string' ? item.file : '');
        const imgUrl = fileKey ? `https://${bucket}.s3.${region}.amazonaws.com/public/${fileKey}` : (item.imageUrl || item.image || '');
        const dollarVal = toDollar(item.price);

        return {
          ...item,
          id: item.id,
          description: itemDesc,
          name: itemDesc,
          title: itemDesc,
          quantity: item.quantity || 1,
          price: dollarVal,
          formattedPrice: dollarVal,
          imageUrl: imgUrl,
          image: imgUrl
        };
      });

      const custName = billingDetails.name || (user && user.username ? user.username : 'Customer');
      const formattedTotal = toDollar(totalAmount);
      const formattedSubtotal = toDollar(subtotal);
      const formattedTax = toDollar(taxAmount);

      const emailPayload = {
        amount: formattedTotal,
        price: formattedTotal,
        subtotal: formattedSubtotal,
        tax: formattedTax,
        orderId: orderId || 'N/A',
        orderDate: new Date().toLocaleString('en-US', {
          dateStyle: 'full',
          timeStyle: 'short',
        }),
        billingDetails: billingDetails,
        customerName: custName,
        userName: custName,
        customerEmail: billingDetails.email || (user && user.attributes ? user.attributes.email : ''),
        shippingAddress: billingDetails.address,
        cartItems: emailItems
      };

      const response = await API.post('comlambda', '/sendemail', {
        body: {
          charge: emailPayload
        }
      });
      console.log("Detailed order breakdown email sent via lambda:", response);
    } catch (err) {
      console.error("Error sending order breakdown email via lambda:", err);
    }
  };

  const handleFormSubmit = async ev => {
    ev.preventDefault();

    const usingSavedAddress = selectedAddressId !== 'new';
    const selectedAddress = usingSavedAddress
      ? addresses.find(a => a.id === selectedAddressId)
      : null;

    const billingDetails = usingSavedAddress
      ? {
          name: selectedAddress.fullName,
          email: user.attributes.email,
          address: {
            city: selectedAddress.city,
            line1: selectedAddress.addressLine1,
            state: selectedAddress.state,
            postal_code: selectedAddress.zip
          }
        }
      : {
          name: ev.target.name.value,
          email: ev.target.email.value,
          address: {
            city: ev.target.city.value,
            line1: ev.target.address.value,
            state: ev.target.state.value,
            postal_code: ev.target.zip.value
          }
        };

    const createShippingAddress = usingSavedAddress
      ? {
          city: selectedAddress.city,
          country: selectedAddress.country,
          address_line1: selectedAddress.addressLine1,
          address_zip: selectedAddress.zip,
          address_state: selectedAddress.state,
          phone_number: selectedAddress.phoneNumber,
        }
      : {
          city: ev.target.city.value,
          country: "usa",
          address_line1: ev.target.address.value,
          address_zip: ev.target.zip.value,
          address_state: ev.target.state.value,
          phone_number: (ev.target.tel && ev.target.tel.value) || "",
        };


    setProcessingTo(true);

    const effectiveDiscount = appliedPromo ? appliedPromo.discountAmount : 0;
    const finalPayablePrice = Math.max(0, parseFloat(price) - effectiveDiscount);

    const DISABLE_STRIPE = true;
    let paymentIntentId = `demo_pi_${Date.now()}`;

    try {
      if (!DISABLE_STRIPE) {
        const cardElement = elements ? elements.getElement("card") : null;
        const { clientSecret } = await API.post('comlambda', '/charge', {
          body: {
            charge: {
              amount: parseFloat(finalPayablePrice),
            }
          }
        });

        const paymentMethodReq = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: billingDetails
        });

        if (paymentMethodReq.error) {
          console.log("paymentMethodReq Error", paymentMethodReq.error.message);
          setCheckoutError(paymentMethodReq.error.message);
          setProcessingTo(false);
          return;
        }

        const { paymentIntent, error } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: paymentMethodReq.paymentMethod.id
        });

        if (error) {
          console.log("error.message", error.message);
          setCheckoutError(error.message);
          setProcessingTo(false);
          return;
        }
        paymentIntentId = paymentIntent.id;
      }

      const input = {
        UserOrders: user.attributes.sub,
        price: parseFloat(finalPayablePrice),
        shipped: false,
        tax_price: parseFloat(tax),
        shipping_price: parseFloat(price),
        init_price: parseFloat(price),
        status: "Processing",
        paymentIntent: paymentIntentId,
        shippingAddress: createShippingAddress,
      };

      const order = await API.graphql(
        graphqlOperation(createOrder, { input })
      )

      const createdOrderId = order.data.createOrder.id;

      // Send detailed itemized email breakdown via Lambda
      await sendOwnerEmail({
        orderId: createdOrderId,
        totalAmount: finalPayablePrice,
        subtotal: total,
        taxAmount: tax,
        billingDetails: billingDetails,
        items: cartItems
      });


      console.log({ order })
      cartItems.map(async product => {
        console.log("product", product);

        // Decrement product inventory stock
        try {
          const currentStock = product.stockQuantity !== undefined && product.stockQuantity !== null ? product.stockQuantity : 50;
          const newStock = Math.max(0, currentStock - (product.quantity || 1));
          await API.graphql(
            graphqlOperation(updateProduct, {
              input: {
                id: product.id,
                stockQuantity: newStock
              }
            })
          );
        } catch (stockErr) {
          console.error("Error updating product stock quantity:", stockErr);
        }

        const file = {
          key: product.file.key,
          bucket: aws_exports.aws_user_files_s3_bucket,
          region: aws_exports.aws_project_region
        }
  
        const input = {
          file,
          orderMyProduct: order.data.createOrder.id,
          description: product.description,
          price: product.price,
          quantity: product.quantity,
          shipped: product.shipped,
          instock:true,
          owner: product.owner,
        }

        console.log("product_input", input);
        const productOrder = await API.graphql(
          graphqlOperation(createMyProduct, { input })
        )
      })

      if (!usingSavedAddress && saveNewAddress) {
        try {
          await API.graphql(
            graphqlOperation(createAddress, {
              input: {
                label: addresses.length === 0 ? 'Home' : `Address ${addresses.length + 1}`,
                fullName: billingDetails.name,
                addressLine1: billingDetails.address.line1,
                city: billingDetails.address.city,
                state: billingDetails.address.state,
                zip: billingDetails.address.postal_code,
                country: "USA",
                phoneNumber: (ev.target.tel && ev.target.tel.value) || "",
                isDefault: addresses.length === 0,
              }
            })
          )
        } catch (error) {
          console.error('Error saving address for future use', error)
        }
      }



      const orderData = {
        id: createdOrderId,
        price,
        subtotal: total,
        tax,
        billingDetails,
        items: [...cartItems]
      };
      setCompletedOrder(orderData);
      setShowReceiptModal(true);

      Notification({
        title: 'Success',
        message: `Order placed Successfully! Detailed email sent.`,
        type: 'success',
        duration: 4000,
      });

      onSuccessfulCheckout();
    } catch (err) {
      console.log("error.message hi", err.message);
      setCheckoutError(err.message);
    }
  };

  // Learning
  // A common ask/bug that users run into is:
  // How do you change the color of the card element input text?
  // How do you change the font-size of the card element input text?
  // How do you change the placeholder color?
  // The answer to all of the above is to use the `style` option.
  // It's common to hear users confused why the card element appears impervious
  // to all their styles. No matter what classes they add to the parent element
  // nothing within the card element seems to change. The reason for this is that
  // the card element is housed within an iframe and:
  // > styles do not cascade from a parent window down into its iframes

  const iframeStyles = {
    base: {
      color: "#1f2d3d",
      fontSize: "16px",
      iconColor: "#01bf71",
      "::placeholder": {
        color: "#aab4bf"
      }
    },
    invalid: {
      iconColor: "#FFC7EE",
      color: "#E25950"
    },
    complete: {
      iconColor: "#cbf4c9"
    }
  };

  const cardElementOpts = {
    iconStyle: "solid",
    style: iframeStyles,
    hidePostalCode: true
  };

  return (
    <FormCard onSubmit={handleFormSubmit}>
      <SectionTitle>Shipping address</SectionTitle>
      {addresses.length > 0 && (
        <AddressOptions>
          {addresses.map(address => (
            <AddressOption key={address.id} $selected={selectedAddressId === address.id}>
              <input
                type="radio"
                name="savedAddress"
                checked={selectedAddressId === address.id}
                onChange={() => setSelectedAddressId(address.id)}
              />
              <AddressOptionBody>
                <AddressOptionLabel>
                  {address.label}
                  {address.isDefault && <DefaultTag>Default</DefaultTag>}
                </AddressOptionLabel>
                <AddressOptionLine>
                  {address.fullName} · {address.addressLine1}, {address.city}, {address.state} {address.zip}
                </AddressOptionLine>
              </AddressOptionBody>
            </AddressOption>
          ))}
          <AddressOption $selected={selectedAddressId === 'new'}>
            <input
              type="radio"
              name="savedAddress"
              checked={selectedAddressId === 'new'}
              onChange={() => setSelectedAddressId('new')}
            />
            <AddressOptionBody>
              <AddressOptionLabel>Ship to a new address</AddressOptionLabel>
            </AddressOptionBody>
          </AddressOption>
        </AddressOptions>
      )}

      {selectedAddressId === 'new' && (
        <>
          <BillingDetailsFields />
          <SaveAddressRow>
            <input
              type="checkbox"
              checked={saveNewAddress}
              onChange={e => setSaveNewAddress(e.target.checked)}
            />
            Save this address for next time
          </SaveAddressRow>
        </>
      )}

      <SectionTitle>Promo code</SectionTitle>
      {!appliedPromo ? (
        <PromoGroup>
          <input
            type="text"
            placeholder="e.g. WELCOME10"
            value={promoCodeInput}
            onChange={e => {
              setPromoCodeInput(e.target.value);
              setPromoError("");
            }}
          />
          <button type="button" onClick={handleApplyPromo}>
            Apply
          </button>
        </PromoGroup>
      ) : (
        <AppliedBadge>
          <span>🏷️ Code <strong>{appliedPromo.code}</strong> applied ({appliedPromo.label})</span>
          <button type="button" onClick={handleRemovePromo}>
            Remove
          </button>
        </AppliedBadge>
      )}
      {promoError && <PromoMessage $error>{promoError}</PromoMessage>}
      {promoSuccess && <PromoMessage>{promoSuccess}</PromoMessage>}

      <SectionTitle>Payment</SectionTitle>
      {stripeDisabled ? (
        <div style={{ background: 'rgba(255, 154, 60, 0.12)', border: '1px solid rgba(255, 154, 60, 0.3)', padding: '14px 18px', borderRadius: '12px', color: '#c96a12', fontWeight: '700', fontSize: '0.92rem', marginBottom: '16px' }}>
          <span role="img" aria-label="lightning">⚡</span> <strong>Instant Demo Checkout</strong>: Stripe live card processing is temporarily paused. Click Place Order below to submit your order.
        </div>
      ) : (
        <CardBox>
          <CardElement options={cardElementOpts} onChange={handleCardDetailsChange} />
        </CardBox>
      )}

      {checkoutError && <CheckoutError>{checkoutError}</CheckoutError>}

      <ButtonWrap>
        <SubmitButton disabled={isProcessing}>
          {isProcessing ? "Processing..." : `Place Order (${formatNumber(appliedPromo ? Math.max(0, price - appliedPromo.discountAmount) : price)})`}
        </SubmitButton>
      </ButtonWrap>

      <SecureNote>
        <span role="img" aria-label="lock">🔒</span> Payments are secure &amp; encrypted
      </SecureNote>

      {/* Post-Checkout Order Tax Invoice Confirmation Modal */}
      {showReceiptModal && completedOrder && (
        <TaxInvoiceModal
          order={completedOrder}
          onClose={() => {
            setShowReceiptModal(false);
            onSuccessfulCheckout();
          }}
        />
      )}
    </FormCard>
  );
};

export default CheckoutForm;
